import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { plantContractService } from "../services/plant-contract.service";
import type {
	CreatePlantContractRequest,
	ErrorResponse,
	PlantContractListParams,
	UpdatePlantContractRequest,
} from "../types";

export const plantContractKeys = {
	all: ["plant-contracts"] as const,
	lists: () => [...plantContractKeys.all, "list"] as const,
	list: (params?: PlantContractListParams) => [...plantContractKeys.lists(), params] as const,
	details: () => [...plantContractKeys.all, "detail"] as const,
	detail: (id: string) => [...plantContractKeys.details(), id] as const,
};

export function usePlantContractList(params?: PlantContractListParams) {
	return useQuery({
		queryKey: plantContractKeys.list(params),
		queryFn: () => plantContractService.list(params),
		// organizationId is required by backend; only fetch when present
		enabled: params?.organizationId === undefined ? true : !!params.organizationId,
	});
}

export function usePlantContract(id: string) {
	return useQuery({
		queryKey: plantContractKeys.detail(id),
		queryFn: () => plantContractService.getById(id),
		enabled: !!id,
	});
}

export function useCreatePlantContract() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreatePlantContractRequest) => plantContractService.create(data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: plantContractKeys.lists() });
			if (variables.organizationId) {
				queryClient.invalidateQueries({
					queryKey: plantContractKeys.list({ organizationId: variables.organizationId }),
				});
			}
			message.success("Shartnoma muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			message.error(error.response?.data?.error?.message || "Shartnoma yaratishda xatolik");
		},
	});
}

export function useUpdatePlantContract() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdatePlantContractRequest }) =>
			plantContractService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: plantContractKeys.lists() });
			queryClient.invalidateQueries({ queryKey: plantContractKeys.detail(variables.id) });
			message.success("Shartnoma muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			message.error(error.response?.data?.error?.message || "Shartnoma yangilashda xatolik");
		},
	});
}

export function useDeletePlantContract() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => plantContractService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: plantContractKeys.lists() });
			message.success("Shartnoma muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			message.error(error.response?.data?.error?.message || "Shartnoma o'chirishda xatolik");
		},
	});
}
